<template>
    <div class="email-template-editor">
        <div class="toolbar" v-if="!previewMode">
            <div class="left-buttons">
                <SupsisButton @click="$emit('close')" primary small> &lt; {{ $t("message.goBack") }}</SupsisButton>
                <SupsisButton @click="handleUpdateTemplate" primary small>
                    <i class="fas fa-save"></i> {{$t("message.update")}}
                </SupsisButton>
            </div>
            <div class="right-buttons">
                <SupsisButton @click="handleMjmlModalOpen">MJML</SupsisButton>
                <SupsisButton @click="handleHtmlModalOpen">HTML {{$t("message.show")}}</SupsisButton>
                <SupsisButton @click="handleModelModalOpen">Model {{$t("message.show")}}</SupsisButton>
                <TemplateSelector @template-select="handleTemplateSelect" />
            </div>
        </div>

        <div ref="editor" class="grape-wrapper"></div>

        <template v-if="!previewMode">
            <Modal2 v-if="mjmlModalOpen" title="MJML" @cancel="mjmlModalOpen = false">
                {{ editor && editor.getHtml() }}
            </Modal2>

            <Modal2 v-if="htmlModalOpen" title="HTML" @cancel="htmlModalOpen = false">
                {{ getRawHtml() }}
            </Modal2>

            <Modal2 v-if="modelModalOpen" title="Model" @cancel="modelModalOpen = false">
                {{ editor && JSON.stringify(editor.getComponents()) }}
            </Modal2>
        </template>
    </div>
</template>

<script>
import Vue from "vue";
import grapesjs from "grapesjs";
import gjsPresetNewsletter from "grapesjs-preset-newsletter";
import "grapesjs/dist/css/grapes.min.css";
import gjsMjml from "grapesjs-mjml";
import mjml2html from "mjml-browser";
import TemplateSelector from "./TemplateSelector.vue";
import Modal2 from "../../../../components/common/Modal2.vue";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import { MAIL_API } from "../../../../api/mail";

export default Vue.extend({
    name: "EmailTemplateEditor",
    components: {
        TemplateSelector,
        Modal2,
        SupsisButton,
    },
    props: {
        template: {
            type: Object,
            required: true,
        },
        previewMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            editor: null,
            mjmlModalOpen: false,
            htmlModalOpen: false,
            modelModalOpen: false,
        };
    },
    mounted() {
        let design = this.template.design;
        if (typeof design === "string") {
            design = JSON.parse(design);
        }

        const config = {
            container: this.$refs.editor,
            plugins: [gjsPresetNewsletter, gjsMjml],
            pluginsOpts: {
                gjsPresetNewsletter: {},
                gjsMjml: {},
            },
            components: design,
            style: `
                .mj-text {
                    padding: 20px;
                }
            `,
            storageManager: false,
        }
        if (this.previewMode) {
            config.canvas =  {};
            config.commands = {};
            config.panels = {};
            config.deviceManager = {};
            config.selectorManager = {};
            config.editable = false;
            config.noticeOnUnload = false;
            config.showOffsets = false;
            config.showRulers = false;
            config.allowScripts = false;
            config.canvas.styles = [];
            config.canvas.scripts = [];
            config.commands.defaults = [];
            config.panels.defaults = [];
            config.deviceManager.devices = [];
            config.selectorManager.componentFirst = false;
        }
        const editor = grapesjs.init(config);

        this.editor = editor;

        if (this.previewMode) {
            editor.getModel().set('draftMode', false);
            editor.Commands.stop('preview');
            editor.getModel().set('hoverable', false);
            editor.getModel().set('selectable', false);
            editor.getModel().set('draggable', false);
            editor.getModel().set('droppable', false);
            editor.getModel().set('highlightable', false);
            editor.refresh();

            editor.UndoManager.clear();
            editor.Panels.getPanels().reset();
            editor.getContainer().classList.add('gjs-preview-mode');
        }

        window.editor = editor;
    },
    beforeDestroy() {
        if (this.editor) {
            this.editor.destroy();
        }
    },
    methods: {
        handleMjmlModalOpen() {
            this.mjmlModalOpen = true;
        },
        handleHtmlModalOpen() {
            this.htmlModalOpen = true;
        },
        handleModelModalOpen() {
            this.modelModalOpen = true;
        },
        handleTemplateSelect(template) {
            this.editor.setComponents(template);
        },
        getRawHtml() {
            try {
                return mjml2html(this.editor?.getHtml())?.html;
            } catch (error) {
                console.error(error);
                return "";
            }
        },
        handleUpdateTemplate() {
            const updatedHtml = this.getRawHtml();
            const updatedDesign = this.editor.getComponents();

            const updatingFields = {
                html: updatedHtml,
                design: updatedDesign,
            };

            MAIL_API.TEMPLATE.updateEmailTemplate(this.template._id, updatingFields)
                .then((updatedTemplate) => {
                    this.$message.success(this.$t("mail.templateUpdateSucs"));
                    this.$emit("template-updated", updatedTemplate);
                })
                .catch((error) => {
                    console.error("Şablon güncellenirken bir hata oluştu:", error);
                    this.$message.error(this.$t("mail.templateUpdateError"));
                });
        },
    },
});
</script>

<style lang="scss">
.email-template-editor {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;

    .grape-wrapper {
        height: calc(100vh - 156px) !important;
        max-height: calc(100vh - 156px) !important;

        &.preview-mode {
            height: 100vh !important;
            max-height: 100vh !important;
        }
    }

    .toolbar {
        margin-bottom: 10px;
        display: flex;
        justify-content: space-between;
        gap: 10px;

        .left-buttons,
        .right-buttons {
            display: flex;
            gap: 10px;
        }
    }

    iframe {
        height: 100% !important;
    }

    &:deep(.gjs-cv-canvas) {
        width: 100%;
        height: 100%;
        top: 0;
    }

    &:deep(.gjs-pn-panel) {
        padding: 0;
    }

    .gjs-preview-mode {
        .gjs-pn-panels,
        .gjs-dev-name,
        .gjs-frame-wrapper--no-select,
        .gjs-badges,
        .gjs-toolbar,
        .gjs-tools,
        .gjs-resizer,
        .gjs-highlighter,
        .gjs-offset-viewer,
        .gjs-selected {
            display: none !important;
        }

        .gjs-cv-canvas {
            width: 100% !important;
            height: 100% !important;
            left: 0 !important;
            top: 0 !important;
            padding: 0 !important;
        }

        .gjs-frame {
            border: none !important;
        }

        .gjs-cv-canvas-bg {
            width: 100% !important;
            height: 100% !important;
            top: 0 !important;
        }
    }
}
</style>
